/** @jsxImportSource @emotion/react */
// React/core
import React from 'react'
// Third-party
import { css } from '@emotion/react'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import Icon from 'src/components/Icon'

// ─── Types ─────────────────────────────────────────────────────

type HeatIntensity = 0 | 1 | 2 | 3

interface HeatmapData {
  /** 8 rows (time slots) x 7 columns (days Sun-Sat), intensity 0-3 */
  grid: HeatIntensity[][]
  yLabels: string[]
  xLabels: string[]
}

interface LineChartDataset {
  label: string
  color: string
  /** 7 values (one per day), percentage 0-100 */
  values: number[]
}

interface LineChartData {
  datasets: LineChartDataset[]
  xLabels: string[]
  yLabels: string[]
}

interface AnalyticsChartsProps {
  heatmapData?: HeatmapData
  lineChartData?: LineChartData
  onExportHeatmap?: () => void
  onExportLineChart?: () => void
}

// ─── Default Data ──────────────────────────────────────────────

const DEFAULT_HEATMAP: HeatmapData = {
  yLabels: [
    '6:00 PM',
    '5:00 PM',
    '4:00 PM',
    '3:00 PM',
    '2:00 PM',
    '1:00 PM',
    '12:00 PM',
    '11:00 AM',
  ],
  xLabels: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  grid: [
    // Each row = time slot (6PM down to 11AM), each col = day (Sun-Sat)
    [0, 0, 0, 0, 0, 0, 0], // 6PM
    [0, 0, 1, 1, 0, 0, 0], // 5PM
    [0, 0, 2, 2, 1, 0, 0], // 4PM
    [0, 1, 3, 3, 3, 1, 0], // 3PM
    [1, 3, 1, 1, 2, 1, 1], // 2PM
    [3, 2, 1, 0, 2, 3, 3], // 1PM
    [2, 1, 0, 0, 1, 1, 2], // 12PM
    [1, 0, 0, 0, 0, 0, 1], // 11AM
  ],
}

const DEFAULT_LINE_CHART: LineChartData = {
  yLabels: ['100%', '80%', '60%', '40%', '20%', '0%'],
  xLabels: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  datasets: [
    {
      label: '16u',
      color: colors.REPROCESSING_BLUE,
      values: [10, 55, 30, 50, 35, 80, 15],
    },
    {
      label: '16u',
      color: colors.SUCCESS_200,
      values: [40, 35, 65, 20, 25, 35, 75],
    },
    {
      label: '16u',
      color: colors.PRIMARY_300,
      values: [70, 15, 45, 35, 60, 20, 70],
    },
  ],
}

// ─── Heatmap Intensity Colors ──────────────────────────────────

const HEAT_COLORS: Record<HeatIntensity, string> = {
  0: '#113f74',
  1: colors.INFO_500,
  2: colors.INFO_300,
  3: colors.INFO_100,
}

// ─── Component ─────────────────────────────────────────────────

const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({
  heatmapData = DEFAULT_HEATMAP,
  lineChartData = DEFAULT_LINE_CHART,
  onExportHeatmap,
  onExportLineChart,
}) => {
  return (
    <div css={containerStyles}>
      {/* Game Time Distribution - Heatmap */}
      <div css={panelStyles}>
        <div css={panelHeaderStyles}>
          <p css={panelTitleStyles}>GAME TIME DISTRIBUTION</p>
          <button
            type='button'
            css={exportButtonStyles}
            onClick={onExportHeatmap}
          >
            <Icon name='download' fontSize={16} color={colors.PRIMARY_300} />
            <span css={exportTextStyles}>Export</span>
          </button>
        </div>
        <div css={chartContainerStyles}>
          <div css={heatmapStyles}>
            {/* Y-axis labels */}
            <div css={yAxisStyles}>
              {heatmapData.yLabels.map((label) => (
                <span key={label} css={axisLabelStyles}>
                  {label}
                </span>
              ))}
            </div>
            {/* Grid */}
            <div css={heatmapGridStyles}>
              <div css={heatmapGridInnerStyles}>
                {heatmapData.grid.map((row, ri) => (
                  <div key={ri} css={heatmapRowStyles}>
                    {row.map((intensity, ci) => (
                      <div
                        key={ci}
                        css={heatmapCellStyles}
                        style={{ backgroundColor: HEAT_COLORS[intensity] }}
                      />
                    ))}
                  </div>
                ))}
              </div>
              {/* X-axis labels */}
              <div css={xAxisStyles}>
                {heatmapData.xLabels.map((label) => (
                  <span key={label} css={axisLabelStyles}>
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Days With More Games - Line Chart */}
      <div css={panelStyles}>
        <div css={panelHeaderStyles}>
          <p css={panelTitleStyles}>DAYS WITH MORE GAMES</p>
          <button
            type='button'
            css={exportButtonStyles}
            onClick={onExportLineChart}
          >
            <Icon name='download' fontSize={16} color={colors.PRIMARY_300} />
            <span css={exportTextStyles}>Export</span>
          </button>
        </div>
        <div css={chartContainerStyles}>
          <div css={lineChartStyles}>
            {/* Y-axis */}
            <div css={lineYAxisStyles}>
              {lineChartData.yLabels.map((label) => (
                <span key={label} css={axisLabelStyles}>
                  {label}
                </span>
              ))}
            </div>
            {/* Chart area */}
            <div css={lineChartAreaStyles}>
              <svg
                css={svgStyles}
                viewBox='0 0 600 200'
                preserveAspectRatio='none'
              >
                {/* Grid lines */}
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <line
                    key={i}
                    x1={0}
                    y1={i * 40}
                    x2={600}
                    y2={i * 40}
                    stroke='#3a4659'
                    strokeWidth={1}
                  />
                ))}
                {/* Data lines */}
                {lineChartData.datasets.map((dataset, di) => {
                  const points = dataset.values
                    .map((v, i) => {
                      const x = i * 100
                      const y = 200 - v * 2
                      return `${x},${y}`
                    })
                    .join(' ')
                  return (
                    <React.Fragment key={di}>
                      <polyline
                        fill='none'
                        stroke={dataset.color}
                        strokeWidth={2}
                        points={points}
                      />
                      {dataset.values.map((v, i) => (
                        <circle
                          key={i}
                          cx={i * 100}
                          cy={200 - v * 2}
                          r={4}
                          fill={dataset.color}
                        />
                      ))}
                    </React.Fragment>
                  )
                })}
              </svg>
              {/* X-axis */}
              <div css={xAxisStyles}>
                {lineChartData.xLabels.map((label) => (
                  <span key={label} css={axisLabelStyles}>
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {/* Legend */}
          <div css={legendStyles}>
            {lineChartData.datasets.map((dataset, i) => (
              <div key={i} css={legendItemStyles}>
                <div
                  css={legendDotStyles}
                  style={{ backgroundColor: dataset.color }}
                />
                <span
                  css={css`
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    font-weight: 700;
                    line-height: 20.3px;
                    color: ${colors.WHITE};
                  `}
                >
                  {dataset.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Styles ────────────────────────────────────────────────────

const containerStyles = css`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  width: 100%;
`

const panelStyles = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

const panelHeaderStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const panelTitleStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 16.8px;
  letter-spacing: 1.68px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_30};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const exportButtonStyles = css`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid ${colors.PRIMARY_300};
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(242, 180, 0, 0.1);
  }
`

const exportTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.PRIMARY_300};
`

const chartContainerStyles = css`
  background: #212935;
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  height: 280px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

// ─── Heatmap Styles ────────────────────────────────────────────

const heatmapStyles = css`
  display: flex;
  flex: 1;
  gap: 8px;
  overflow: hidden;
`

const yAxisStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
  padding-bottom: 26px;
`

const heatmapGridStyles = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const heatmapGridInnerStyles = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const heatmapRowStyles = css`
  display: flex;
  flex: 1;
  gap: 8px;
`

const heatmapCellStyles = css`
  flex: 1;
  border-radius: 2px;
  min-width: 0;
`

const xAxisStyles = css`
  display: flex;
  justify-content: space-between;
  padding: 0 32px;
  flex-shrink: 0;
`

// ─── Line Chart Styles ─────────────────────────────────────────

const lineChartStyles = css`
  display: flex;
  flex: 1;
  gap: 8px;
  overflow: hidden;
`

const lineYAxisStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
  padding-bottom: 26px;
`

const lineChartAreaStyles = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const svgStyles = css`
  flex: 1;
  width: 100%;
`

const axisLabelStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_50};
  white-space: nowrap;
`

// ─── Legend Styles ──────────────────────────────────────────────

const legendStyles = css`
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  flex-shrink: 0;
`

const legendItemStyles = css`
  display: flex;
  align-items: center;
  gap: 4px;
`

const legendDotStyles = css`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
`

export default AnalyticsCharts
